import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Fleet Nervous System - live feed of cross-ship hive-bus events (threats,
// scans, check-ins, insight designations) landing on the mothership's ledger.

const SEVERITY = {
  critical: { dot: "bg-rose-400", text: "text-rose-300", border: "border-rose-400/25", bg: "bg-rose-500/5" },
  warn: { dot: "bg-amber-400", text: "text-amber-300", border: "border-amber-400/25", bg: "bg-amber-500/5" },
  info: { dot: "bg-cyan-400", text: "text-cyan-300", border: "border-white/10", bg: "bg-white/[0.02]" },
};

function relTime(iso) {
  if (!iso) return "";
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function payloadPreview(payload) {
  if (!payload || Object.keys(payload).length === 0) return null;
  const s = Object.entries(payload)
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");
  return s.length > 140 ? s.slice(0, 140) + "…" : s;
}

export default function HiveBusFeed() {
  const [feed, setFeed] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sevFilter, setSevFilter] = useState("");
  const [shipFilter, setShipFilter] = useState("");
  const seenCriticalRef = useRef(null);
  const toastCooldownRef = useRef({}); // ship|type -> last toast ms (10 min cooldown)

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const params = new URLSearchParams({ limit: "40" });
      if (sevFilter) params.set("severity", sevFilter);
      if (shipFilter) params.set("ship", shipFilter);
      const res = await api.get(`/hive/bus-feed?${params}`);
      setFeed(res.data);
      const criticals = (res.data?.events || []).filter((e) => e.severity === "critical");
      if (seenCriticalRef.current === null) {
        seenCriticalRef.current = new Set(criticals.map((e) => e.id));
      } else {
        criticals.forEach((e) => {
          if (!seenCriticalRef.current.has(e.id)) {
            seenCriticalRef.current.add(e.id);
            const k = `${e.ship_from}|${e.type}`;
            const last = toastCooldownRef.current[k] || 0;
            if (Date.now() - last > 600000) {
              toastCooldownRef.current[k] = Date.now();
              toast.error(`FLEET CRITICAL - ${e.ship_from}: ${e.type}`, { duration: 10000 });
            }
          }
        });
      }
    } catch (e) { /* admin-only; ignore */ }
    if (manual) setRefreshing(false);
  }, [sevFilter, shipFilter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="hive-bus-feed">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-emerald-400"
            animate={{ opacity: [0.35, 1, 0.35] }}
            transition={{ duration: 1.6, repeat: Infinity }}
          />
          <Radio className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">
            Fleet Nervous System
          </span>
        </div>
        <div className="flex items-center gap-2">
          {feed && (
            <span
              data-testid="hive-bus-wired-count"
              className="text-[9px] font-mono-data uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-400/25 rounded-full px-2 py-0.5"
              title="Ships that have emitted on the hive bus / total fleet"
            >
              {feed.wired_ships}/{feed.fleet_size} on bus
            </span>
          )}
          <button
            data-testid="hive-bus-refresh"
            onClick={() => load(true)}
            title="Refresh feed now"
            className="text-slate-500 hover:text-emerald-300 transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {feed && (
        <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="hive-bus-filters">
          {["", "info", "warn", "critical"].map((s) => (
            <button
              key={s || "all"}
              data-testid={`hive-bus-filter-severity-${s || "all"}`}
              onClick={() => setSevFilter(s)}
              className={`text-[8px] font-mono-data uppercase tracking-widest rounded-full px-2 py-0.5 border transition-colors ${
                sevFilter === s
                  ? "text-white bg-white/15 border-white/40"
                  : "text-slate-500 border-white/10 hover:text-slate-300"
              }`}
            >
              {s || "all"}
            </button>
          ))}
          <select
            data-testid="hive-bus-filter-ship"
            value={shipFilter}
            onChange={(e) => setShipFilter(e.target.value)}
            className="ml-auto text-[8px] font-mono-data uppercase tracking-widest bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5 text-slate-400 focus:outline-none focus:border-emerald-400/40 max-w-[140px]"
          >
            <option value="">All ships</option>
            {(feed.ships || []).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      )}

      {!feed ? (
        <p className="text-[10px] font-mono-data text-slate-600">Listening for fleet traffic...</p>
      ) : feed.events.length === 0 ? (
        <p className="text-[10px] font-mono-data text-slate-600 leading-relaxed">
          {sevFilter || shipFilter
            ? "No events match this filter."
            : "No cross-ship events yet. Every ship on the hive bus files threats, scans and check-ins here the moment they happen."}
        </p>
      ) : (
        <div className="flex flex-col gap-1.5 max-h-[320px] overflow-y-auto pr-1" data-testid="hive-bus-event-list">
          <AnimatePresence initial={false}>
            {feed.events.map((e) => {
              const sev = SEVERITY[e.severity] || SEVERITY.info;
              const preview = payloadPreview(e.payload);
              return (
                <motion.div
                  key={e.id}
                  data-testid={`hive-bus-event-${e.id}`}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-lg border ${sev.border} ${sev.bg} px-2.5 py-2`}
                >
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sev.dot}`} />
                    <span className="text-[9px] font-mono-data uppercase tracking-widest text-white/90 font-semibold">
                      {e.ship_from}
                    </span>
                    <span className={`text-[9px] font-mono-data ${sev.text}`}>{e.type}</span>
                    <span className="text-[8px] font-mono-data text-slate-600 ml-auto shrink-0">{relTime(e.ts)}</span>
                  </div>
                  {preview && (
                    <p className="text-[9px] font-mono-data text-slate-500 mt-1 leading-relaxed break-all">{preview}</p>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
      <p className="text-[8px] leading-relaxed text-slate-600 font-mono-data mt-2">
        Live ledger of the hive bus - every docked ship emits and absorbs fleet events (threats, scans, check-ins, designated insights) through this channel.
      </p>
    </div>
  );
}
